import apiCache from './apiCache';
import { getUserLocation } from './locationUtils';
import { getUserEmail, getUserName } from './userUtils';
import { detectCreditCard } from './paymentUtil';
import { getToken } from '../actions/ccbillAction';
import { saveCard } from '../actions/cardAction';
import { reportError } from '../sentry';

/**
 * Default form values for CCBill payment forms
 */
export const getDefaultFormValues = () => ({
  firstName: '',
  lastName: '',
  nameOnCard: '',
  email: '',
  address1: '',
  city: '',
  state: '',
  zipcode: '',
  country: 'US',
  cardNumber: '',
  expMonth: '',
  expYear: '',
  cvv2: '',
  ipAddress: ''
});

/**
 * Load CCBill Advanced Widget script if not already loaded
 */
export const loadCCBillWidget = () => {
  const scriptId = 'ccbill-widget';
  if (!document.getElementById(scriptId)) {
    const script = document.createElement('script');
    script.id = scriptId;
    script.src = 'https://js.ccbill.com/v1.9.0/ccbill-advanced-widget.js';
    script.async = true;
    document.body.appendChild(script);
  }
};

/**
 * Fetch account information from API
 * @returns {Promise<Object|null>} Account data or null if not available
 */
export const fetchAccountData = async () => {
  const accessToken = localStorage.getItem('accessToken');
  if (accessToken) {
    try {
      const accountData = await apiCache.getAccountInformation();
      if (accountData && accountData.status === 200 && accountData?.data && accountData?.data?._id) {
        return accountData.data;
      } else {
        return null;
      }
    } catch (apiError) {
      reportError(apiError, 'ccbillFormUtils fetch account');
      return null;
    }
  }
  return null;
};

/**
 * Initialize form with user data from API or localStorage
 */
export const initializeFormWithUserData = async (form, accountData = null, options = {}) => {
  const { isEditMode = false, editCardData = null } = options;
  const defaultValues = getDefaultFormValues();

  try {
    // Reset form to default values first
    form.reset(defaultValues);

    // If in edit mode, pre-fill with edit data
    if (isEditMode && editCardData) {
      form.reset({
        ...defaultValues,
        country: editCardData.country || 'US',
        city: editCardData.city || '',
        state: editCardData.state || ''
      });
      return;
    }

    // Try to get user data from API
    if (accountData && accountData?._id) {
      const userData = accountData;
      const formValues = {
        ...defaultValues,
        firstName: userData.first_name || '',
        lastName: userData.last_name || '',
        nameOnCard: userData.first_name && userData.last_name
          ? `${userData.first_name} ${userData.last_name}`.trim()
          : '',
        email: userData.email || '',
        city: userData.city || '',
        country: userData.country || 'US',
        state: userData.state || ''
      };

      form.reset(formValues);
      
      // Fetch IP address
      try {
        const ipAddress = await apiCache.getIPAddress();
        form.setValue('ipAddress', ipAddress);
      } catch (err) {
        // Silent fail for IP address
      }
      return;
    }

    // Fallback to localStorage data
    await initializeFormFromLocalStorage(form);
  } catch (err) {
    reportError(err, 'ccbillFormUtils init form');
    form.reset({
      ...form.getValues(),
      country: 'US'
    });
  }
};

/**
 * Initialize form from localStorage data
 */
const initializeFormFromLocalStorage = async (form) => {
  try {
    const defaultValues = getDefaultFormValues();
    const userEmail = getUserEmail();
    const userName = getUserName();

    const formValues = {
      ...defaultValues,
      email: userEmail || '',
      firstName: userName.firstName || '',
      lastName: userName.lastName || '',
      nameOnCard: userName.firstName && userName.lastName
        ? `${userName.firstName} ${userName.lastName}`.trim()
        : ''
    };

    // Get user location and set country/state
    const location = await getUserLocation();
    if (location.success) {
      formValues.country = location.country;
      formValues.state = location.state;
    }

    form.reset(formValues);

    // Fetch IP address
    try {
      const ipAddress = await apiCache.getIPAddress();
      form.setValue('ipAddress', ipAddress);
    } catch (err) {
      // Silent fail for IP address
    }
  } catch (err) {
    reportError(err, 'ccbillFormUtils init localStorage');
    form.reset({
      ...form.getValues(),
      country: 'US'
    });
  }
};

/**
 * Create CCBill payment token
 */
export const createCCBillPaymentToken = async () => {
  try {
    // Get access token
    const tokenResult = await getToken();
    if (!tokenResult.success || !tokenResult.data?.access_token) {
      return { success: false, error: 'Failed to retrieve access token.' };
    }

    // Check if CCBill widget is loaded
    if (!window.ccbill) {
      return { success: false, error: 'CCBill widget not loaded yet. Please refresh.' };
    }

    const accessToken = tokenResult.data.access_token;
    const widget = new window.ccbill.CCBillAdvancedWidget(process.env.REACT_APP_CCBILL_CLIENT_ID);
    
    const clientAccnum = process.env.REACT_APP_CCBILL_ACCOUNT 
      ? process.env.REACT_APP_CCBILL_ACCOUNT.toString() 
      : "";
    const clientSubacc = process.env.REACT_APP_CCBILL_SUBACCOUNT 
      ? process.env.REACT_APP_CCBILL_SUBACCOUNT.toString() 
      : "";

    const tokenResponse = await widget.createPaymentToken(
      accessToken,
      Number(clientAccnum),
      Number(clientSubacc)
    );
    const tokenData = await tokenResponse.json();

    if (!tokenData.paymentTokenId) {
      const errorMessage = tokenData.generalMessage || tokenData.error || 'Failed to generate payment token.';
      return { success: false, error: errorMessage };
    }

    return { success: true, paymentTokenId: tokenData.paymentTokenId, accessToken };
  } catch (error) {
    reportError(error, 'ccbillFormUtils payment token');
    return { success: false, error: 'Failed to generate payment token.' };
  }
};

/**
 * Save card to backend
 */
export const saveCardToBackend = async (cardData, options = {}) => {
  const { existingCardsCount = 0 } = options;
  
  try {
    const detected = detectCreditCard(cardData.cardNumber);
    const last4 = cardData.cardNumber.slice(-4);
    const expMonth = Number(cardData.expMonth);
    const expYear = Number(cardData.expYear);
    const isDefault = existingCardsCount === 0;

    const payload = {
      card_token: cardData.paymentTokenId,
      card_brand: detected.type,
      last4,
      exp_month: expMonth,
      exp_year: expYear,
      is_default: isDefault,
      ...(cardData.firstName != null && { first_name: cardData.firstName }),
      ...(cardData.lastName != null && { last_name: cardData.lastName }),
      ...(cardData.country != null && { country: cardData.country }),
      ...(cardData.stateCode != null && { state_code: cardData.stateCode })
    };

    const saveResult = await saveCard(payload);
    return saveResult;
  } catch (error) {
    reportError(error, 'ccbillFormUtils save card');
    return { success: false, error: error?.error || error?.message || 'Failed to save card.' };
  }
};

